import React from 'react';
import styled from 'styled-components';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  TextField
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { ConfigFieldControl } from '@sierralabs/core-ui';
import { QueryBuilderPluginConfig } from '@sierralabs/query-core';

import { ColumnType } from '@sierralabs/entity-core';
import { useEntityContext } from '@sierralabs/entity-ui';
import { tableCase } from '@sierralabs/core';
import {
  QueryBuilderReactPlugin,
  useQueryBuilderContext
} from '@sierralabs/query-ui';

const StyledConfigFieldControl = styled(ConfigFieldControl)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export enum DateFormat {
  Day = 'day',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year'
}

export enum DatasetType {
  Single = 'Single',
  Multiple = 'Multiple'
}

export interface LineChartQueryBuilderParams {
  datasetType: DatasetType;
  field?: {
    columnName?: string;
    columnType?: ColumnType;
    format?: DateFormat;
    displayAllDates?: boolean;
  };
  fieldLegend?: {
    columnName?: string;
    columnType?: ColumnType;
  };
  chartHeight?: number;
  showLegends?: boolean;
  forWorkflowStatus?: boolean;
  axisTitle?: {
    show?: boolean;
    XTitle?: string;
    YTitle?: string;
  };
  excludeNullValues?: boolean;
}

export const LineChartQueryBuilder: React.FC<
  QueryBuilderPluginConfig<LineChartQueryBuilderParams>
> = props => {
  const { type, params } = props;
  const { queryManager } = useQueryBuilderContext();
  const { entities } = useEntityContext();
  const index = (queryManager.query?.config?.pluginConfigs || []).findIndex(
    pluginConfig => pluginConfig.type === type
  );

  const update = (updateParams: Partial<LineChartQueryBuilderParams> = {}) => {
    queryManager.updatePluginConfig(index, {
      type: LineChartQueryBuilderPlugin.type,
      params: { ...params, ...updateParams }
    });
  };

  return (
    <>
      <StyledConfigFieldControl>
        <TextField
          fullWidth
          label='Select Format'
          select
          size='small'
          variant='outlined'
          className='select-field'
          value={params?.datasetType || null}
          onChange={event => {
            const { value } = event.target;
            if (!!value) {
              params.datasetType = value as DatasetType;
              update();
            }
          }}
        >
          {[DatasetType.Single, DatasetType.Multiple].map(value => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        <FormHelperText>Select the Chart Dataset Type</FormHelperText>
      </StyledConfigFieldControl>
      <StyledConfigFieldControl>
        <Autocomplete
          fullWidth
          clearOnBlur
          clearOnEscape
          value={params.field?.columnName || null}
          options={queryManager.queryAliasColumnMap.map(
            columnMap => columnMap.path
          )}
          onChange={(_, value) => {
            params.field = {
              columnName: value || undefined
            };
            if (value) {
              // get selected column type
              const [alias, property] = value.split('.');
              params.field.columnType = entities
                ?.find(entity => entity.tableName === tableCase(alias))
                ?.columns.find(column => column.name === property)?.type;
            }
            update();
          }}
          renderInput={params => (
            <TextField
              {...params}
              fullWidth
              size='small'
              variant='outlined'
              label='Select Field'
            />
          )}
        />
        <FormHelperText>
          Select the field value to be displayed on the chart X axis (Bottom)
        </FormHelperText>
      </StyledConfigFieldControl>
      {['timestamp', 'date'].includes(
        params?.field?.columnType || ('' as any)
      ) && (
        <>
          <StyledConfigFieldControl>
            <TextField
              fullWidth
              label='Select Format'
              select
              size='small'
              variant='outlined'
              className='select-field'
              value={params?.field?.format || DateFormat.Month}
              onChange={event => {
                const { value } = event.target;
                if (!!value) {
                  params.field = {
                    ...params.field,
                    format: value as DateFormat
                  };
                  update();
                }
              }}
            >
              {[
                DateFormat.Day,
                DateFormat.Month,
                DateFormat.Quarter,
                DateFormat.Year
              ].map(value => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            <FormHelperText>
              Date format for field {params.field?.columnName}
            </FormHelperText>
          </StyledConfigFieldControl>
          <StyledConfigFieldControl>
            <FormControlLabel
              label='Display All Dates'
              control={
                <Checkbox
                  size='small'
                  color='primary'
                  className='checkbox'
                  checked={!!params.field?.displayAllDates}
                  onChange={() => {
                    params.field = {
                      ...params.field,
                      displayAllDates: !params.field?.displayAllDates
                    };
                    update();
                  }}
                />
              }
            />
            <FormHelperText>
              This will display all dates even the value is 0
            </FormHelperText>
          </StyledConfigFieldControl>
        </>
      )}
      {params.datasetType === DatasetType.Multiple && (
        <StyledConfigFieldControl>
          <Autocomplete
            fullWidth
            clearOnBlur
            clearOnEscape
            value={params.fieldLegend?.columnName || null}
            options={queryManager.queryAliasColumnMap.map(
              columnMap => columnMap.path
            )}
            onChange={(_, value) => {
              params.fieldLegend = {
                columnName: value || undefined
              };
              if (value) {
                // get selected column type
                const [alias, property] = value.split('.');
                params.fieldLegend.columnType = entities
                  ?.find(entity => entity.tableName === tableCase(alias))
                  ?.columns.find(column => column.name === property)?.type;
              }
              update();
            }}
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                size='small'
                variant='outlined'
                label='Select Field Legend'
              />
            )}
          />
          <FormHelperText>
            Select the field value that will be used as Legend indicator
          </FormHelperText>
        </StyledConfigFieldControl>
      )}
      <StyledConfigFieldControl>
        <TextField
          type='number'
          fullWidth
          size='small'
          variant='outlined'
          label='Chart Height'
          value={params.chartHeight}
          onChange={event => {
            const value = event.target.value
              ? parseInt(event.target.value)
              : undefined;
            params.chartHeight = value;
            update();
          }}
        />
        <FormHelperText>Custom Chart Height (Default: 300px)</FormHelperText>
      </StyledConfigFieldControl>
      <ConfigFieldControl>
        <FormControlLabel
          label='Show Legends'
          control={
            <Checkbox
              size='small'
              color='primary'
              className='checkbox'
              checked={!!params.showLegends}
              onChange={() => {
                params.showLegends = !params.showLegends;
                update();
              }}
            />
          }
        />
      </ConfigFieldControl>
      <StyledConfigFieldControl>
        <FormControlLabel
          label='Exclude Null Values'
          control={
            <Checkbox
              size='small'
              color='primary'
              className='checkbox'
              checked={!!params.excludeNullValues}
              onChange={() => {
                params.excludeNullValues = !params.excludeNullValues;
                update();
              }}
            />
          }
        />
        <FormHelperText>
          This will exclude null values on both Field (X Axis) and FieldLegend
        </FormHelperText>
      </StyledConfigFieldControl>
      <StyledConfigFieldControl>
        <FormControlLabel
          label='For Workflow Status'
          control={
            <Checkbox
              size='small'
              color='primary'
              className='checkbox'
              checked={!!params.forWorkflowStatus}
              onChange={() => {
                params.forWorkflowStatus = !params.forWorkflowStatus;
                update();
              }}
            />
          }
        />
        <FormHelperText>
          Enable the parsing of Task Status values
        </FormHelperText>
      </StyledConfigFieldControl>
      <StyledConfigFieldControl>
        <FormControlLabel
          label='Show Axis Titles'
          control={
            <Checkbox
              size='small'
              color='primary'
              className='checkbox'
              checked={!!params.axisTitle?.show}
              onChange={event => {
                params.axisTitle = {
                  ...params.axisTitle,
                  show: event.target.checked
                };
                update();
              }}
            />
          }
        />
        <FormHelperText>Show the title on both X and Y Axis</FormHelperText>
      </StyledConfigFieldControl>
      {params?.axisTitle?.show && (
        <>
          <StyledConfigFieldControl>
            <TextField
              type='text'
              fullWidth
              size='small'
              variant='outlined'
              label='X Axis Title'
              value={params.axisTitle?.XTitle}
              onChange={event => {
                const value = event.target.value;
                params.axisTitle = {
                  ...params.axisTitle,
                  XTitle: value
                };
                update();
              }}
            />
          </StyledConfigFieldControl>
          <StyledConfigFieldControl>
            <TextField
              type='text'
              fullWidth
              size='small'
              variant='outlined'
              label='Y Axis Title'
              value={params.axisTitle?.YTitle}
              onChange={event => {
                const value = event.target.value;
                params.axisTitle = {
                  ...params.axisTitle,
                  YTitle: value
                };
                update();
              }}
            />
          </StyledConfigFieldControl>
        </>
      )}
    </>
  );
};

export const LineChartQueryBuilderPlugin: QueryBuilderReactPlugin = {
  name: 'Line Chart',
  type: 'LineChart',
  component: LineChartQueryBuilder
};
